import { Ref, ref } from 'vue';
import { useKeyboardNavigation } from '@/utils/composables/useSearchSuggestionKeyboard';

export type Props<T, U extends keyof T> = {
  inputValue: Ref<string>;
  histories: Ref<T[]>;
  displayKeyName: U;
  historyRefs: Ref<HTMLElement[] | undefined>;
  emit: ((evt: 'delete-history', index: number) => void) &
    ((evt: 'select-from-input') => void) &
    ((evt: 'select-from-history', index: number) => void);
  enableShiftEnterNewline?: boolean; // Shift + Enter で改行できるようにするか
};
/**
 * 履歴の表示/選択/削除機能を提供する
 *
 * @returns isHistoryEnabled: 履歴の表示が有効か
 * @returns deleteHistory: index指定で履歴を削除する関数
 * @returns selectHistory: index指定で履歴を選択する関数
 * @returns focusIndex: キーボード操作時のフォーカス位置
 * @returns handlers: イベントハンドラ
 */
export function useHistoryDropdownInput<T, U extends keyof T>({
  inputValue,
  histories,
  displayKeyName,
  historyRefs,
  emit,
  enableShiftEnterNewline,
}: Props<T, U>) {
  const isHistoryEnabled = ref(false);
  const originalValue = ref<string>();

  // 入力変換中かどうか
  const isComposing = ref(false);
  const onCompositionStart = () => {
    isHistoryEnabled.value = false;
    isComposing.value = true;
  };
  const onCompositionEnd = () => {
    isComposing.value = false;
  };

  const resetState = () => {
    isHistoryEnabled.value = false;
  };
  const resetFocus = () => (originalValue.value = inputValue.value);

  // キーボード操作のhooks
  const {
    focusIndex,
    onKeyDown: onKeyDownHistory,
    resetIndex,
  } = useKeyboardNavigation();
  const selectHistory = (index: number) => {
    submit(index);
    resetState();
    resetIndex();
  };

  const submit = async (index: number) => {
    if (histories.value) {
      if (index === -1 && inputValue.value) {
        emit('select-from-input');
      } else if (histories.value[index]) {
        emit('select-from-history', index);
      }
    }
  };

  const onKeyDown = (event: KeyboardEvent) => {
    // 入力変換を確定する際に送信されないように早期リターン
    if (isComposing.value) return;
    if (isHistoryEnabled.value) {
      // Shift + Enter で改行できるように設定されている場合はEnterキーの処理をスキップ
      if (event.key === 'Enter' && event.shiftKey && enableShiftEnterNewline) {
        return;
      }
      onKeyDownHistory(
        event,
        histories.value ? histories.value.length : 0,
        selectHistory,
      );
      if (focusIndex.value === -1) {
        inputValue.value = originalValue.value ?? '';
      } else {
        inputValue.value = histories.value?.[focusIndex.value][
          displayKeyName
        ] as string;
        const history = historyRefs.value?.find(
          b => Number(b.dataset.index) === focusIndex.value,
        );
        history?.scrollIntoView({
          block: 'nearest',
        });
      }
    } else {
      if (event.key === 'Enter') {
        // Shiftキーが押されてない、もしくはShift + Enter で改行できるように設定されていない場合は送信
        if (!event.shiftKey || !enableShiftEnterNewline) {
          emit('select-from-input');
        }
      }
    }
  };
  const updateDropdownState = (value: string) => {
    // 入力値が空の場合は履歴を表示する
    isHistoryEnabled.value = value.trim() === '';
  };
  const onFocus = () => {
    updateDropdownState(inputValue.value);
  };
  const onInput = (event: Event) => {
    const target = event.target as HTMLInputElement;
    if (target) {
      inputValue.value = target.value;
      originalValue.value = target.value;
      updateDropdownState(target.value);
      resetIndex();
    }
  };

  const onBlur = (event: FocusEvent) => {
    // blurですぐに閉じてしまうとdropdown上のクリックイベントが拾えないので少しずらす
    setTimeout(() => {
      const relatedTarget = event.relatedTarget as HTMLElement;
      if (
        !relatedTarget ||
        !relatedTarget.classList.contains('delete-button')
      ) {
        resetState();
        resetFocus();
        resetIndex();
      }
    }, 200);
  };

  const deleteHistory = async (index: number) => {
    inputValue.value = '';
    resetFocus();
    resetIndex();
    emit('delete-history', index);
  };

  const handlers = {
    input: onInput,
    focus: onFocus,
    compositionstart: onCompositionStart,
    compositionend: onCompositionEnd,
    blur: onBlur,
    keydown: onKeyDown,
  };

  return {
    isHistoryEnabled,
    deleteHistory,
    selectHistory,
    focusIndex,
    handlers,
  };
}
